<script setup lang="ts">
import MyCart from '@/api/MyCart'
import PaymentFee from '@/api/PaymentFee'
import ShippingRate from '@/api/ShippingRate'
import PaymentGateway from '@/constants/payment-gateway'
import useAuthStore from '@/stores/auth'
import useCartStore, { CartIntent } from '@/stores/cart'
import useGatewayStore from '@/stores/gateway'
import useShippingMethodStore from '@/stores/shipping-method'
import { watchDebounced } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const gatewayStore = useGatewayStore()
const shippingMethodStore = useShippingMethodStore()
const { selectedItem } = storeToRefs(cartStore)

watchDebounced(
  () => cartStore.intent,
  async (intent) => {
    switch (intent) {
      case CartIntent.Get:
        await fetchCart()
        break

      case CartIntent.Synchronize:
        await synchronizeCart()
        break

      case CartIntent.Reset:
        await resetCart()
        break

      case CartIntent.AddItem:
        await addCartItem()
        break

      case CartIntent.UpdateItem:
        await updateCartItem()
        break

      case CartIntent.DeleteItem:
        await deleteCartItem()
        break

      case CartIntent.CheckoutItem:
        await checkoutItem()
        break

      default:
        break
    }

    cartStore.intent = null
    cartStore.selectedItem = null
  },
  { debounce: 500 }
)

watch(
  () => route.name,
  (routeName) => {
    if (routeName === 'checkout') {
      fetchShippingRates()
      calculatePaymentFee()
    }
  },
  { immediate: true }
)

onMounted(() => {
  fetchCart()
})

const calculatePaymentFee = async () => {
  cartStore.termsAndConditions = false
  gatewayStore.loading = true

  try {
    const data = await PaymentFee.calculate<PaymentFeeResponse>({
      include_platform_fee: true,
      item_subtotal: cartStore.cart.selected_item_subtotal,
      shipping_cost: cartStore.cart.shipping_cost
    })

    gatewayStore.providerFees = data.provider_fees
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  } finally {
    gatewayStore.loading = false
  }
}

const fetchShippingRates = async () => {
  if (!authStore.isAuthenticated || !authStore.isCustomer) {
    return
  }

  shippingMethodStore.loading = true

  try {
    shippingMethodStore.providers = await ShippingRate.get<ShippingProvider[]>()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  } finally {
    shippingMethodStore.loading = false
  }
}

const fetchCart = async () => {
  if (!authStore.isAuthenticated || !authStore.isCustomer) {
    return
  }

  try {
    cartStore.cart = await MyCart.get<Cart>()
    if ('checkout' === route.name) {
      await calculatePaymentFee()
    }
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}

const synchronizeCart = async () => {
  if (!cartStore.cart.items) {
    return
  }

  cartStore.loading = true

  try {
    const syncedCart = await MyCart.sync<Cart>({
      items: cartStore.cart.items,
      payment_gateway: cartStore.cart.payment_gateway ?? PaymentGateway.PAYPAL,
      shipping_address_id: cartStore.cart.shipping_address?.id,
      shipping_provider: cartStore.cart.shipping_provider,
      shipping_service: cartStore.cart.shipping_service
    })

    if (syncedCart) {
      cartStore.cart = syncedCart

      if ('checkout' === route.name) {
        await calculatePaymentFee()
        await fetchShippingRates()
      }
    }
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  } finally {
    cartStore.loading = false
  }
}

const resetCart = async () => {
  try {
    await MyCart.reset()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}

const addCartItem = async () => {
  if (!selectedItem.value?.product?.id) {
    return
  }

  try {
    await MyCart.storeItem({
      product_id: selectedItem.value.product.id,
      quantity: selectedItem.value.quantity,
      notes: selectedItem.value.notes
    })
    await synchronizeCart()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}

const updateCartItem = async () => {
  if (!selectedItem.value?.id) {
    return
  }

  try {
    await MyCart.updateItem(selectedItem.value.id, {
      quantity: selectedItem.value.quantity,
      notes: selectedItem.value.notes
    })
    await synchronizeCart()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}

const deleteCartItem = async () => {
  if (!selectedItem.value?.id) {
    return
  }

  try {
    await MyCart.deleteItem(selectedItem.value.id)
    await synchronizeCart()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}

const checkoutItem = async () => {
  try {
    cartStore.checkingOut = true
    await synchronizeCart()
    router.push({ name: 'checkout' })
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  } finally {
    cartStore.checkingOut = false
  }
}
</script>

<template>
  <div class="hidden"></div>
</template>
